import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'dios10_cookie_consent';
const CONSENT_VERSION = 1;

const defaultConsent = {
  version: CONSENT_VERSION,
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
  timestamp: null
};

const loadConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.version !== CONSENT_VERSION) return null;
    return data;
  } catch {
    return null;
  }
};

const saveConsent = (consent) => {
  try {
    const toSave = {
      ...consent,
      version: CONSENT_VERSION,
      timestamp: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    return toSave;
  } catch {
    return consent;
  }
};

const CookieConsentContext = createContext(null);

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsentState] = useState(() => loadConsent());

  const setConsent = useCallback((next) => {
    setConsentState((prev) => {
      const updated = typeof next === 'function' ? next(prev || defaultConsent) : next;
      const saved = saveConsent(updated);
      return saved;
    });
  }, []);

  const hasConsent = consent !== null;

  const acceptAll = useCallback(() => {
    setConsent({
      ...defaultConsent,
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
      timestamp: Date.now()
    });
  }, [setConsent]);

  const rejectOptional = useCallback(() => {
    setConsent({
      ...defaultConsent,
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
      timestamp: Date.now()
    });
  }, [setConsent]);

  const updatePreferences = useCallback((prefs) => {
    setConsent((prev) => ({
      ...(prev || defaultConsent),
      necessary: true,
      analytics: Boolean(prefs.analytics),
      marketing: Boolean(prefs.marketing),
      preferences: Boolean(prefs.preferences),
      timestamp: Date.now()
    }));
  }, [setConsent]);

  const openPreferences = useCallback(() => {
    window.dispatchEvent(new CustomEvent('dios10:openCookiePreferences'));
  }, []);

  const value = useMemo(() => ({
    consent,
    hasConsent,
    acceptAll,
    rejectOptional,
    updatePreferences,
    openPreferences,
    setConsent
  }), [consent, hasConsent, acceptAll, rejectOptional, updatePreferences, openPreferences, setConsent]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
};
